import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import OnboardingModal from "../components/OnboardingModal";

const DM_SANS = "'DM Sans', system-ui, sans-serif";
const NAV_DARK = "#0c2c54";

export default function OnboardingSuccess() {
  const [searchParams]            = useSearchParams();
  const navigate                  = useNavigate();
  const { loginWithToken }        = useAuth();           // ← use loginWithToken, not login
  const [status, setStatus]       = useState("verifying"); // "verifying" | "ready" | "pending" | "error"
  const [errorMsg, setErrorMsg]   = useState("");
  const [pendingMsg, setPendingMsg] = useState("");
  const [showModal, setShowModal] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const confirm = async () => {
      try {
        // Preview: visit /onboarding/success?preview=1 to show the welcome modal
        // without a real Stripe session. Remove before shipping.
        if (searchParams.get("preview") === "1") {
          setStatus("ready");
          setShowModal(true);
          return;
        }
        const sessionId = searchParams.get("session_id");
        if (!sessionId) throw new Error("No session ID in URL.");

        // 1. Confirm payment with backend.
        //    The backend retries up to 5× internally to handle Stripe's brief
        //    delay, then falls back to the DB if the webhook already activated
        //    the account.
        const res  = await fetch(`${API_URL}/subscription/confirm-session?session_id=${sessionId}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.message || "Payment confirmation failed.");

        // 3. Clean up session storage
        sessionStorage.removeItem("pendingRegistration");

        // AXO AI / Enterprise: card saved, no dashboard access or charge
        // until AXO has completed the bespoke service mapping.
        if (data.pendingOnboarding) {
          setPendingMsg(data.message || "");
          setStatus("pending");
          return;
        }

        if (!data.token) throw new Error("Your account could not be activated yet.");

        // 2. Store the JWT and set user state, no extra API call needed.
        loginWithToken(data.token);

        // 4. Show onboarding modal
        setStatus("ready");
        setShowModal(true);

      } catch (err) {
        console.error("[OnboardingSuccess]", err);
        setErrorMsg(err.message || "Something went wrong confirming your payment.");
        setStatus("error");
      }
    };

    confirm();
  }, []); // eslint-disable-line

  const handleDismiss = () => {
    setShowModal(false);
    navigate("/dashboard", { replace: true });
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  if (status === "verifying") {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
        fontFamily: DM_SANS,
      }}>
        <div style={{ textAlign: "center", color: "#ffffff" }}>
          <svg
            style={{ width: 32, height: 32, margin: "0 auto 16px", display: "block",
              animation: "spin 1s linear infinite" }}
            fill="none" viewBox="0 0 24 24"
          >
            <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
            <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.2)" strokeWidth="4"/>
            <path fill="rgba(255,255,255,0.8)"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
          </svg>
          <p style={{ fontSize: 15, color: "rgba(255,255,255,0.65)", margin: 0 }}>
            Confirming your payment…
          </p>
        </div>
      </div>
    );
  }

  // ── Pending onboarding state ──────────────────────────────────────────────
  if (status === "pending") {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
        fontFamily: DM_SANS, padding: 24,
      }}>
        <div style={{
          background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.14)",
          borderRadius: 14, padding: "32px 34px", maxWidth: 460, width: "100%", textAlign: "center",
        }}>
          <div style={{
            width: 44, height: 44, margin: "0 auto 16px", borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(52,211,153,0.12)", color: "#34d399",
          }}>
            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p style={{ fontSize: 17, fontWeight: 700, color: "#fff", marginBottom: 8 }}>
            Card saved — you're in the onboarding queue
          </p>
          <p style={{ fontSize: 13, lineHeight: 1.6, color: "rgba(255,255,255,0.62)", marginBottom: 22 }}>
            {pendingMsg ||
              "Our team is now mapping your services to the AXO scoring model. We'll email you as soon as your account is live and billing starts."}
          </p>
          <p style={{ fontSize: 11.5, color: "rgba(255,255,255,0.38)", margin: 0 }}>
            Billing has not started. You'll only be charged once onboarding is complete.
          </p>
        </div>
      </div>
    );
  }

  // ── Error state ────────────────────────────────────────────────────────────
  if (status === "error") {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
        fontFamily: DM_SANS, padding: 24,
      }}>
        <div style={{
          background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)",
          borderRadius: 12, padding: "28px 32px", maxWidth: 420, width: "100%", textAlign: "center",
        }}>
          <svg style={{ width: 32, height: 32, margin: "0 auto 16px", color: "#fca5a5", display: "block" }}
            fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          </svg>
          <p style={{ fontSize: 15, fontWeight: 600, color: "#ffffff", marginBottom: 8 }}>
            Something went wrong
          </p>
          <p style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", marginBottom: 24 }}>
            {errorMsg}
          </p>
          <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
            <button
              onClick={() => navigate("/signup")}
              style={{
                fontFamily: DM_SANS, fontSize: 13, fontWeight: 600,
                background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
                color: "rgba(255,255,255,0.7)", borderRadius: 8, padding: "9px 18px", cursor: "pointer",
              }}
            >
              Back to signup
            </button>
            <button
              onClick={() => navigate("/login")}
              style={{
                fontFamily: DM_SANS, fontSize: 13, fontWeight: 600,
                background: "#ffffff", border: "none",
                color: NAV_DARK, borderRadius: 8, padding: "9px 18px", cursor: "pointer",
              }}
            >
              Try logging in
            </button>
          </div>
          <p style={{ fontSize: 11, color: "rgba(255,255,255,0.3)", marginTop: 20 }}>
            If your payment completed, your account is active, try logging in directly.
          </p>
        </div>
      </div>
    );
  }

  // ── Success state, modal overlays a minimal background ───────────────────
  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(145deg, #0c2c54 0%, #0e3363 50%, #0a2d58 100%)",
    }}>
      {showModal && <OnboardingModal onDismiss={handleDismiss} />}
    </div>
  );
}